import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Compass, TrendingUp, TrendingDown, ArrowUpRight, ArrowRight } from 'lucide-react';

const ord = (n) => {
  const v = n % 100;
  const suffix = (v >= 11 && v <= 13) ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] || 'th');
  return `${n}${suffix}`;
};

// Benchmark KPI metric → Solution Finder KPI id (the Find →
// Prioritise → Act bridge). Only kpi_gaps metrics are actionable in
// Solution Finder; LEI/ROI gaps have no direct KPI mapping.
const BENCH_TO_SF_KPI = {
  productivity: 'productivity',
  quality: 'quality',
  employee_engagement: 'engagement',
  employee_retention: 'mobility',
  customer_satisfaction: 'csat',
  safety_incidents: 'safety',
};

/**
 * NavigationAdvantageCard — small synthesis panel for the Industry
 * Benchmarks overview. No new calculations: reads the existing
 * my-position comparisons to surface strongest area, largest gap and
 * headroom to the top quartile.
 */
export default function NavigationAdvantageCard({ myPosition, industryName }) {
  const navigate = useNavigate();
  const comps = myPosition?.comparisons || [];
  if (!myPosition?.has_data || comps.length === 0) return null;

  const strongest = comps.reduce((a, b) => (b.percentile_rank > a.percentile_rank ? b : a));
  const weakest = comps.reduce((a, b) => (b.percentile_rank < a.percentile_rank ? b : a));
  const avg = myPosition.summary?.average_percentile_rank ?? 0;
  const improvement = Math.max(0, Math.round(75 - avg));

  // Actionable gap: the weakest KPI comparison that Solution Finder
  // can model directly (prefer the overall weakest if it maps).
  const kpiGaps = comps
    .filter((c) => BENCH_TO_SF_KPI[c.metric_id])
    .sort((a, b) => a.percentile_rank - b.percentile_rank);
  const actionableGap = kpiGaps[0] || null;

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="navigation-advantage-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 ring-1 ring-indigo-200">
            <Compass className="h-4 w-4 text-indigo-600" />
          </span>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-700">Navigation Advantage</p>
        </div>
        <p className="text-sm font-semibold text-slate-900 mt-3" data-testid="navigation-advantage-headline">
          Your organisation outperforms {Math.round(avg)}% of {industryName || 'industry'} organisations.
        </p>
        <div className="space-y-2 mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-slate-500"><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> Strongest Area</span>
            <span className="font-semibold text-slate-900 text-right" data-testid="navigation-strongest">
              {strongest.metric_name} <span className="text-emerald-600">({ord(Math.round(strongest.percentile_rank))})</span>
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-slate-500"><TrendingDown className="h-3.5 w-3.5 text-amber-600" /> Largest Gap</span>
            <span className="font-semibold text-slate-900 text-right" data-testid="navigation-largest-gap">
              {weakest.metric_name} <span className="text-amber-600">({ord(Math.round(weakest.percentile_rank))})</span>
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-slate-500"><ArrowUpRight className="h-3.5 w-3.5 text-indigo-600" /> Potential Improvement</span>
            <span className="font-semibold text-indigo-700" data-testid="navigation-improvement">
              {improvement > 0 ? `+${improvement} percentile positions to top quartile` : 'Top quartile — maintain your advantage'}
            </span>
          </div>
        </div>
        {actionableGap && (
          <button
            type="button"
            onClick={() => navigate(`/solution-finder?kpi=${BENCH_TO_SF_KPI[actionableGap.metric_id]}`)}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-2 transition-colors"
            data-testid="navigation-gap-cta"
          >
            Close the {actionableGap.metric_name} gap in Solution Finder
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </CardContent>
    </Card>
  );
}
